import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Heart, Eye, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { CARD, TH, TD, TYPE_COLORS, num, dateText, Avatar, TierBadge, TypeBadge, BTN_OUTLINE } from './caShared';

const Donut = ({ breakdown, total }) => (
  <div className={`${CARD} p-5`} data-testid="post-type-donut">
    <div className="text-sm font-semibold text-[#111827] dark:text-white mb-2">Deliverable Mix</div>
    <div className="relative h-48">
      <ResponsiveContainer><PieChart>
        <Pie data={breakdown.filter((b) => b.count)} dataKey="count" nameKey="label" innerRadius={55} outerRadius={80} paddingAngle={2} stroke="none">
          {breakdown.filter((b) => b.count).map((b) => <Cell key={b.type} fill={TYPE_COLORS[b.type]} />)}
        </Pie><Tooltip formatter={(v, n) => [`${v} posts`, n]} />
      </PieChart></ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"><div className="text-2xl font-bold text-[#111827] dark:text-white">{total}</div><div className="text-[11px] text-[#6B7280]">posts</div></div>
    </div>
    <div className="flex justify-center gap-4 mt-2">{breakdown.map((b) => <span key={b.type} className="inline-flex items-center gap-1.5 text-xs text-[#4B5563]"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: TYPE_COLORS[b.type] }} />{b.label} ({b.count})</span>)}</div>
  </div>
);

const TypeTable = ({ breakdown }) => (
  <div className={`${CARD} overflow-hidden`}>
    <table className="w-full" data-testid="post-type-table">
      <thead><tr>{['Deliverable Type', 'No. of Posts', 'Avg Views', 'Avg Engagement', 'Avg ER%'].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
      <tbody>{breakdown.map((b) => (
        <tr key={b.type} data-testid={`post-type-row-${b.type}`}>
          <td className={TD}><TypeBadge type={b.type} /></td><td className={`${TD} font-semibold`}>{b.count}</td>
          <td className={TD}>{b.type === 'reel' ? num(b.avg_views) : '—'}</td><td className={TD}>{num(b.avg_engagement)}</td><td className={TD}>{b.count ? `${b.avg_er}%` : '—'}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

const PostCard = ({ p }) => {
  const lead = p.tagged_creators[0];
  return (
    <div className={`${CARD} overflow-hidden flex flex-col`} data-testid={`post-card-${p.id}`}>
      <div className="flex items-center gap-2.5 p-3">
        <Avatar name={lead?.handle} size={32} />
        <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate text-[#111827] dark:text-white">@{lead?.handle || 'unknown'}</div><div className="text-[11px] text-[#9CA3AF]">{dateText(p.posted_at)}{p.tagged_creators.length > 1 ? ` · +${p.tagged_creators.length - 1} more` : ''}</div></div>
        {lead?.in_vyvre_db && <TierBadge tier={lead.tier} />}
      </div>
      <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="relative aspect-[4/5] bg-gradient-to-br from-[#EEF0FB] to-[#DDE2F8] dark:from-white/5 dark:to-white/10 flex items-center justify-center group" data-testid={`post-thumb-${p.id}`}>
        <span className="text-xs text-[#3B4FD8]/60 group-hover:text-[#3B4FD8] inline-flex items-center gap-1 transition-colors"><ExternalLink size={13} /> {p.is_stub ? 'Stub post' : 'Open on Instagram'}</span>
        <span className="absolute top-2 left-2"><TypeBadge type={p.post_type} /></span>
      </a>
      <div className="p-3 flex items-center gap-4 text-xs text-[#4B5563] dark:text-zinc-300">
        <span className="inline-flex items-center gap-1"><Heart size={12} className="text-[#E1306C]" /> {num(p.engagement)}</span>
        {p.views != null && <span className="inline-flex items-center gap-1"><Eye size={12} /> {num(p.views)}</span>}
        <span className="ml-auto font-semibold text-[#3B4FD8]">{p.er_percent}% ER</span>
      </div>
    </div>
  );
};

export const PostAnalysisTab = ({ data, page, setPage }) => (
  <div className="space-y-6" data-testid="post-analysis-tab">
    <div className="grid lg:grid-cols-[320px_1fr] gap-6"><Donut breakdown={data.breakdown} total={data.stats.total_posts} /><TypeTable breakdown={data.breakdown} /></div>
    {!data.posts.length ? <div className={`${CARD} p-8 text-center text-sm text-[#6B7280]`}>No posts in this period.</div> : (
      <>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{data.posts.map((p) => <PostCard key={p.id} p={p} />)}</div>
        {data.total_pages > 1 && (
          <div className="flex items-center justify-center gap-3" data-testid="post-pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className={BTN_OUTLINE} data-testid="post-page-prev"><ChevronLeft size={14} /></button>
            <span className="text-sm text-[#4B5563]">Page {page} of {data.total_pages}</span>
            <button disabled={page >= data.total_pages} onClick={() => setPage(page + 1)} className={BTN_OUTLINE} data-testid="post-page-next"><ChevronRight size={14} /></button>
          </div>
        )}
      </>
    )}
  </div>
);
